import { Popover } from '@/components/utils/Popover/Popover';
import { Box, Stack, TextField } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

const SIZE = {
    width: 360,
    height: 360,
};
const WORD_LIMIT = 300;
interface Props {
    title: string;
    anchorEl: HTMLElement | null;
    value: string;
    onClose: () => void;
    onUpdate: (content: string) => void;
}

export const AudioRecorderReferenceTextPopover: React.FC<Props> = ({ title, anchorEl, value, onClose, onUpdate }) => {
    const { t } = useTranslation();
    const [text, setText] = React.useState(value);
    const [size, setSize] = React.useState(SIZE);
    const [isResizing, setIsResizing] = React.useState(false);
    const [startPos, setStartPos] = React.useState({ x: 0, y: 0 });
    const [startSize, setStartSize] = React.useState(SIZE);

    const open = Boolean(anchorEl);

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
        setStartPos({ x: e.clientX, y: e.clientY });
        setStartSize(size);
    };

    const handleMouseMove = React.useCallback(
        (e: MouseEvent) => {
            if (!isResizing) return;

            const deltaX = e.clientX - startPos.x;
            const deltaY = e.clientY - startPos.y;

            // Use the larger delta to maintain aspect ratio
            const delta = Math.max(deltaX, deltaY);
            const newSize = Math.max(360, startSize.width + delta);

            setSize({ width: newSize, height: newSize });
        },
        [isResizing, startPos, startSize],
    );

    const handleMouseUp = React.useCallback(() => {
        setIsResizing(false);
    }, []);

    React.useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isResizing, handleMouseMove, handleMouseUp]);

    return (
        <Popover
            aria-label='Audio Recorder Reference Text Popover'
            title={title}
            open={open}
            anchorEl={anchorEl}
            onClose={() => onClose()}
            anchorOrigin={{
                vertical: 'center',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'center',
                horizontal: -8,
            }}
            slotProps={{
                paper: {
                    sx: {
                        width: size.width,
                        height: size.height,
                        display: 'flex',
                        flexDirection: 'column',
                        p: 2,
                        position: 'relative',
                    },
                },
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Stack spacing={1.5} sx={{ flex: 1 }}>
                    <TextField
                        multiline
                        value={text}
                        placeholder={t('slide.components.pronunciationAssessment.popover.description')}
                        onChange={(e) => {
                            setText(e.target.value);
                            onUpdate(e.target.value);
                        }}
                        error={text.length > WORD_LIMIT}
                        helperText={
                            text.length >= WORD_LIMIT
                                ? t('slide.components.pronunciationAssessment.popover.error')
                                : `${text.length}/${WORD_LIMIT} ${t('slide.components.pronunciationAssessment.popover.characters')}`
                        }
                        slotProps={{
                            htmlInput: {
                                maxLength: WORD_LIMIT,
                            },
                        }}
                        sx={{
                            flex: 1,
                            '& .MuiInputBase-root': {
                                height: '100%',
                                alignItems: 'flex-start',
                            },
                            '& .MuiInputBase-input': {
                                height: '100% !important',
                                overflow: 'auto !important',
                            },
                            '& .MuiFormHelperText-root': {
                                color: text.length >= WORD_LIMIT ? 'error.main' : 'text.secondary',
                            },
                        }}
                    />
                </Stack>
                {/* Resize Handle */}
                <Box
                    onMouseDown={handleMouseDown}
                    data-testid='resize-handle'
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: 20,
                        height: 20,
                        cursor: 'nw-resize',
                        zIndex: 1000,
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 3,
                            right: 3,
                            width: 0,
                            height: 0,
                            borderLeft: '12px solid transparent',
                            borderBottom: '12px solid #ccc',
                        },
                        '&:hover::after': {
                            borderBottomColor: '#999',
                        },
                    }}
                />
            </Box>
        </Popover>
    );
};
